import { Component, Injector, Input, Renderer2, OnInit } from '@angular/core';
import { UnitSystem } from '../../details/UnitSystem';
import { MatDialog } from '@angular/material';
import { FileNameComponent } from './file-name/file-name.component';
import { TransformToCsvService } from '../../service/transform-to-csv.service';
import { HomeComponent } from '../home/home.component';
import { Router, NavigationStart } from '@angular/router';
import { DataToSaveService } from '../../service/data-to-save.service';
import { PapaParseService } from 'ngx-papaparse';
import { ImportService } from '../../service/import.service';
import { TrackingService } from '../../service/tracking.service';
import { NotificationsService } from '../../service/notifications.service';
import {DialogState, ScrollBarData} from "../shared/scroll-bar-data";
import {AuthGuard} from "../../service/auth-guard.service";

declare const document: {
  documentMode
};

export enum RecommendationType {
  oring_recommendation,
  groove_recommendation,
  design_validation
}

enum GrooveType {
  'Rectangular',
  'Dovetail',
  'Half Dovetail'
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  providers: [TransformToCsvService, ]
})
export class HeaderComponent implements OnInit {

  constructor(private dialog: MatDialog,
              private transformToCsvService: TransformToCsvService, private renderer: Renderer2,
              private inj: Injector, private papa: PapaParseService, private trackingService: TrackingService,
              private router: Router, private importService: ImportService,
              private dataToSaveService: DataToSaveService, private notificationsService: NotificationsService) {
    this.parentComponent = this.inj.get(HomeComponent);
  }

  @Input() measurementUnit: string;
  @Input() product: string;
  data: any;
  calculatedData: any;
  grooveType;
  parentComponent: HomeComponent;
  dataToSave;
  fileError = false;
  notOurFile = false;

  ngOnInit() {
    this.router.events
      .filter(event => event instanceof NavigationStart)
      .subscribe(() => {
        if (this.router.url === '/home' ) {
          this.dataToSaveService.setDataToSave(this.data);
          this.dataToSaveService.setCalculationData(this.calculatedData);
        }
      });
    this.getSavedData();
  }

  openSaveFile() {
    if (this.router.url !== '/home') return;
    if (!this.data || this.dataToSaveService.clearedData) return;
    const data = this.data;
    const calculatedData = this.calculatedData;
    const unitSuffix = UnitSystem.getTypeByUnit(this.measurementUnit);
    const measurementType = () => this.measurementUnit === 'mm' ? 'Metric' : 'English';
    this.grooveType = this.parentComponent.oringComp.grooveType;
    const recommendationType = RecommendationType[this.parentComponent.recommendationType];
    this.dataToSave = {
      'grooveData': {
        'groove_type': GrooveType[this.grooveType],
        'angle': data.grooveData.angle,
        'operating_temp': data.grooveData.operatingTemp,
        'gland_depth': data.grooveData.glandDepth,
        'gland_depth_tol': data.grooveData.glandDepthTol,
        'gland_opening': data.grooveData.glandOpening,
        'gland_opening_tol': data.grooveData.glandOpeningTol,
        'gland_diameter': data.grooveData.grooveDiameter,
        'gland_diameter_tol': data.grooveData.grooveDiameterTol,
        'top_radius': data.grooveData.topRadius,
        'bottom_radius': data.grooveData.bottomRadius,
      }, 'oring_data': {
        'oring': (() => Boolean(data.oringData['Metric(mm)']) ? this.editAsCode(this.data.oringData['Metric(mm)']) : '')(),
        'oring_size_id': data.oringData[`ID-${unitSuffix}`],
        'oring_size_id_tol': data.oringData[`ID Tolerance-${unitSuffix}`],
        'oring_size_cs': data.oringData[`CS-${unitSuffix}`],
        'oring_size_cs_tol': data.oringData[`CS Tolerance-${unitSuffix}`]
      }, 'measurement_type': measurementType(),
      'type': recommendationType
    };
    if (this.parentComponent.recommendationType === 2 && !calculatedData) return;
    const hasScrollbar = window.innerWidth > window.document.documentElement.clientWidth;
    const matDialogRef = this.dialog.open(FileNameComponent);
    ScrollBarData.updateBodyWidth(DialogState.OPEN, hasScrollbar);
    matDialogRef.afterClosed().subscribe(() => ScrollBarData.updateBodyWidth(DialogState.CLOSE));
    if (this.parentComponent.recommendationType === 2 && calculatedData) {
      this.dataToSave.calculatedData = {
        'volume_fill_min': calculatedData.volumeFillMin,
        'volume_fill_nom': calculatedData.volumeFillNom,
        'volume_fill_max': calculatedData.volumeFillMax,
        'operating_squeeze_min': calculatedData.operatingSqueezeMin,
        'operating_squeeze_nom': calculatedData.operatingSqueezeNom,
        'operating_squeeze_sax': calculatedData.operatingSqueezeMax,
        'stretch_min': calculatedData.stretchMin,
        'stretch_nom': calculatedData.stretchNom,
        'stretch_max': calculatedData.stretchMax,
        'squeeze_min': calculatedData.squeezeMin,
        'squeeze_nom': calculatedData.squeezeNom,
        'squeeze_max': calculatedData.squeezeMax
      };
      this.dataToSave.product = this.editProductName(this.product);
    }
    this.dataToSaveService.setData(this.dataToSave);
  }

  exportFile() {
    const data = this.data;
    if (!data || this.dataToSaveService.clearedData) return;
    this.grooveType = this.parentComponent.oringComp.grooveType;
    const calculatedData = this.calculatedData;
    const unitSuffix = UnitSystem.getTypeByUnit(this.measurementUnit);
    const recommendationType = ['Recommendation Type', RecommendationType[this.parentComponent.recommendationType]];
    const measurementType  = () => this.measurementUnit === 'mm' ? 'Metric' : 'English';
    const grooveTitles = ['Groove', 'Angle', 'Operating Temp °C', 'Gland Depth (L)',
          'Gland Depth Tolerance', 'Gland Opening (W)', 'Gland Opening Tolerance', 'Gland Diameter(D)',
          'Gland Diameter Tolerance', 'Top Radius (R2)', 'Bottom Radius (R1)'];
    const grooveValues = [GrooveType[this.grooveType], data.grooveData.angle, data.grooveData.operatingTemp, data.grooveData.glandDepth,
          data.grooveData.glandDepthTol, data.grooveData.glandOpening, data.grooveData.glandOpeningTol,
          data.grooveData.grooveDiameter, data.grooveData.grooveDiameterTol, data.grooveData.topRadius,
          data.grooveData.bottomRadius];
    const oringTitles =  ['O-Ring', 'O-Ring Size (ID)', 'O-Ring Size (ID) Tolerance', 'O-Ring Size (CS)',
          'O-Ring Size (CS) Tolerance'];
    const oringValues = [(() => Boolean(data.oringData['Metric(mm)']) ? this.editAsCode(this.data.oringData['Metric(mm)']) : '')(), data.oringData[`ID-${unitSuffix}`],
          data.oringData[`ID Tolerance-${unitSuffix}`], data.oringData[`CS-${unitSuffix}`],
          data.oringData[`CS Tolerance-${unitSuffix}`]];
    if (this.parentComponent.recommendationType < 2) {
        this.transformToCsvService.exportToCsv(`${RecommendationType[this.parentComponent.recommendationType]}.csv`,
          [grooveTitles, grooveValues, oringTitles, oringValues, ['Measurement Units'], [measurementType()], recommendationType]);
    } else if (this.parentComponent.recommendationType === 2) {
      if (!calculatedData) return;
      this.transformToCsvService.exportToCsv(`${RecommendationType[this.parentComponent.recommendationType]}.csv`, [grooveTitles, grooveValues, oringTitles, oringValues,
        ['Volume Fill Minimum %', 'Volume Fill Nominal %', 'Volume Fill Maximum %', 'Operating Squeeze Min %',
        'Operating Squeeze Nominal %', 'Operating Squeeze Maximum %', 'Stretch Minimum %', 'Stretch Nominal %',
        'Stretch Maximum %', 'Squeeze Minimum %', 'Squeeze Nominal %', 'Squeeze Maximum %'],
        [calculatedData.volumeFillMin, calculatedData.volumeFillNom, calculatedData.volumeFillMax, calculatedData.operatingSqueezeMin,
        calculatedData.operatingSqueezeNom, calculatedData.operatingSqueezeMax, calculatedData.stretchMin, calculatedData.stretchNom,
        calculatedData.stretchMax, calculatedData.squeezeMin, calculatedData.squeezeNom, calculatedData.squeezeMax],
        ['Product'], [this.editProductName(this.product)], ['Measurement Units'], [measurementType()],
        recommendationType]);
    }
    this.trackingService.fileExported();
  }

  editAsCode(asCode) {
    const absolute = Math.abs(asCode);
    if (absolute < 10) {
      return 'AS -00' + absolute;
    } else if (absolute >= 10 && absolute < 100) {
      return 'AS -0' + absolute;
    }
    return 'AS -' + absolute;
  }

  editProductName(product: string) {
    return product.replace('®', '');
  }

  signOut() {
    localStorage.clear();
    AuthGuard.deleteCookieByName('AuthToken');
    AuthGuard.deleteCookieByName('email');
    AuthGuard.deleteCookieByName('isLoggedIn');
    this.router.navigateByUrl('/');
  }

  goToSavedFiles() {
    this.router.navigateByUrl('saved-files');
    this.trackingService.navigatedToSavedFiles();
  }

  inputFile() {
    if (this.router.url !== '/home') return;
    const ie = /*@cc_on!@*/false || !!document.documentMode;
    const inputEl: HTMLInputElement = this.renderer.createElement('input');
    this.renderer.setProperty(inputEl, 'type', 'file');
    inputEl.click();
    if (ie && inputEl.value) {
      this.parseFile(inputEl.files[0]);
    } else {
      inputEl.onchange = () => {
        this.parseFile(inputEl.files[0]);
      };
    }
  }

  parseFile(csvFile) {
    const fileType = csvFile.name.slice(-3);
    if (fileType !== 'csv') {
      this.notificationsService.emitNotification('fileError');
      return;
    }
    this.fileError = false;
    this.papa.parse(csvFile, {
      complete: (results: any) => {
        if (results.data.length !== 7 && results.data.length !== 11) {
          this.notificationsService.emitNotification('notValidFile');
          return;
        }
        this.parentComponent.designComp.clearDesignData();
        Object.keys(this.parentComponent.recommendations).map(item => {
          this.parentComponent.recommendations[item] = true;
        });
        this.notOurFile = false;
        this.importService.setImportResults(results);
        this.trackingService.fileImported();

        this.parentComponent.grooveComp.initImportedData();
        this.parentComponent.oringComp.initImportedData();
        this.parentComponent.designComp.initImportedData();
        this.dataToSaveService.clearedData = false;
      },
      skipEmptyLines: true
    });
  }

  getSavedData(): void {
    const paths = this.dataToSaveService.getPaths();
    if (paths.length > 0 && paths[paths.length - 1] === 'saved-files' && this.router.url !== '/saved-files') {
      this.data = this.dataToSaveService.getSavedData();
      this.calculatedData = this.dataToSaveService.getCalculationData();
      if (this.dataToSaveService.getProduct()) {
        this.product = this.dataToSaveService.getProduct().text;
      }
    }
  }
}



// WEBPACK FOOTER //
// /var/www/asna/front-end/public_html/src/app/components/header/header.component.ts